'use client'

import { useId, useState } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { Shuffle } from 'lucide-react'

import type { FeaturedMoodTile, MoodFilter } from '@/lib/blog/moods'
import { Button } from '@/components/ui/button'

import { openMood } from './mood-filter'

interface WanderByMoodProps {
    /** The featured moods, in selection order — resolved by `getMoodSectionData`. */
    featured: FeaturedMoodTile[]
    /** The non-featured registry remainder the "surprise me" control draws from. */
    surprisePool: MoodFilter[]
}

/**
 * The mood-browsing section on the blog home (blog FR-18): one tile per featured
 * mood — the mood's label over its most-recent post's hero image — plus a
 * "surprise me" control that *reveals* a random NON-featured mood for the reader
 * to click (blog-moods RD-13); repeat clicks re-roll to a different one.
 *
 * Opening a mood (a tile, or the revealed surprise) does not navigate:
 * {@link openMood} sets a `?mood=` param (via `history.pushState`, so Back returns
 * here) and `BlogHomeContent` swaps the whole blog home to that mood's post listing
 * (blog-moods FR-8, RD-15). The tiles and the reveal are `<button>`s, not links, so
 * no crawlable per-mood URL exists. Empty/heroless featured moods were omitted
 * upstream; a fully empty section renders nothing.
 */
export const WanderByMood = ({ featured, surprisePool }: WanderByMoodProps) => {
    const t = useTranslations('blogHome.wanderByMood')
    const headingId = useId()
    const [surprise, setSurprise] = useState<MoodFilter | null>(null)

    if (featured.length === 0) {
        return null
    }

    const reroll = () => {
        // Draw from the pool minus the current reveal so repeat clicks always
        // move (a same-pick re-roll reads as a dead button).
        const pool = surprisePool.filter((mood) => mood.slug !== surprise?.slug)
        const pick = pool[Math.floor(Math.random() * pool.length)] ?? surprise
        setSurprise(pick ?? null)
    }

    return (
        <section
            aria-labelledby={headingId}
            className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-10 md:py-14"
        >
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2
                        id={headingId}
                        className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]"
                    >
                        {t('heading')}
                    </h2>
                    <p className="mt-1 text-[15px] text-text-secondary md:text-base">
                        {t('subheading')}
                    </p>
                </div>
                {surprisePool.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                        {surprise && (
                            <span data-testid="mood-surprise" className="text-[15px] md:text-base">
                                {t('reveal')}{' '}
                                <button
                                    type="button"
                                    onClick={() => openMood(surprise.slug)}
                                    className="font-bold text-text-primary underline underline-offset-4"
                                >
                                    {surprise.label}
                                </button>
                            </span>
                        )}
                        <Button variant="outline" onClick={reroll}>
                            <Shuffle aria-hidden="true" className="size-4" />
                            {t('surpriseMe')}
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
                {featured.map((tile) => (
                    <button
                        key={tile.slug}
                        type="button"
                        onClick={() => openMood(tile.slug)}
                        data-testid="mood-tile"
                        className="relative block h-[130px] overflow-hidden rounded-lg bg-muted text-left shadow-sm transition-shadow hover:shadow-lg md:h-[220px]"
                    >
                        {tile.post.heroImage && (
                            <Image
                                src={tile.post.heroImage.url}
                                alt={tile.post.heroImage.alt}
                                fill
                                unoptimized
                                sizes="(min-width: 768px) 25vw, 50vw"
                                className="object-cover"
                            />
                        )}
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent"
                        />
                        <span className="absolute bottom-3 left-3 text-[15px] font-bold text-background md:text-lg">
                            {tile.label}
                        </span>
                    </button>
                ))}
            </div>
        </section>
    )
}
